import './styles.scss'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Divider, Form, Input, Row } from 'antd'
import type { FormInstance } from 'antd'
import clsx from 'clsx'
import type { ChangeEvent } from 'react'

import B2becButton from '@/components/B2becButton'
import Translator from '@/components/B2becTranslation'
import { B2becButtonBack } from '@/components/CustomButton/B2becButtonIcon'
import { DeliveryAddress } from '@/components/DisplayAddress/DeliveryAddress'
import { NotificationManager } from '@/components/Notification'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectCountryNames,
  selectIsRequiredReference,
  selectRegions,
} from '@/store/selectors/configSelector'
import {
  getSelectedMachineData,
  selectIsSubmitServiceLoading,
  selectSubmitServiceStatus,
} from '@/store/selectors/serviceCaseSelector'
import { selectUserLanguage } from '@/store/selectors/userSelector'
import { selectUserId } from '@/store/slices/authenticationSlice'
import { selectCustomerNumber, selectDeliveryAddresses } from '@/store/slices/customerSlice'
import { createServiceCase } from '@/store/slices/serviceCaseSlice'
import useDeviceDetect from '@/hooks/useDeviceDetect'
import { ACTION_STATUS, SERVICE_CASE_PRIORITY, SERVICE_ENTRY, SERVICE_TYPE } from '@/libs/constants'
import { isValidArray, mergeIntoStringWithSeparator } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import ContactInformation from './components/ContactInformation'
import DamagedPicture from './components/DamagedPicture'
import FailureDescription from './components/FailureDescription'
import SelectedMachine from './components/SelectedMachines'
import SelectedService from './components/SelectedService'
import ServiceBlockWrapper from './components/ServiceBlockWrapper'
import WhatHappensNext from './components/WhatHappensNext'

interface ServiceOverviewStepProps {
  form: FormInstance
  currentTab: number
  handleChangeTab: (...args: unknown[]) => unknown
}

const ServiceOverviewStep = ({
  currentTab,
  handleChangeTab,
  form,
}: ServiceOverviewStepProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const tLabel = (key) => t(`service.serviceOverview.label.${key}`)

  const deliveryAddresses = useAppSelector(selectDeliveryAddresses)
  const isLoadingSubmitServiceCase = useAppSelector(
    selectIsSubmitServiceLoading,
  )
  const submitServiceStatus = useAppSelector(selectSubmitServiceStatus)
  const selectedMachineData = useAppSelector(getSelectedMachineData)
  const currentUserCustomerNumber = useAppSelector(selectCustomerNumber)
  const userId = useAppSelector(selectUserId)
  const countryNamesConfig = useAppSelector(selectCountryNames)
  const language = useAppSelector(selectUserLanguage)
  const isRequiredReference = useAppSelector(selectIsRequiredReference)

  const {
    serviceEntry,
    id: machineId,
    name: machineName,
    serialNumber,
  } = selectedMachineData

  const [furtherNotes, setFurtherNotes] = useState('')
  const [failureDescriptionData, setFailureDescriptionData] = useState([])
  const regions = useAppSelector(selectRegions)

  const selectedFAQs = Form.useWatch('failureDescriptions', form)

  const failureDescriptionNotes = Form.useWatch('failureDescriptionNotes', form)

  const serviceType = Form.useWatch('serviceType', form)

  const selectedDesiredService = useMemo(
    () =>
      serviceEntry !== SERVICE_ENTRY.MACHINE
        ? serviceEntry
        : serviceType,
    [serviceEntry, serviceType],
  )

  const partnerNumber = currentUserCustomerNumber
  const deliveryAddress = Form.useWatch('deliveryAddress', form)
  const damagedImages = Form.useWatch('damagedImages', form)

  const { isMobile } = useDeviceDetect()

  const contactPersonFirstName = Form.useWatch('contactPersonFirstName', form)
  const contactPersonLastName = Form.useWatch('contactPersonLastName', form)
  const contactPersonEmail = Form.useWatch('contactEmail', form)
  const contactPersonPhoneNumber = Form.useWatch('contactNumber', form)

  const contactInformation = {
    firstName: contactPersonFirstName,
    lastName: contactPersonLastName,
    email: contactPersonEmail,
    phoneNumber: contactPersonPhoneNumber,
  }

  // only navigate for a submission this component actually initiated —
  // the store can still hold a SUCCESS from an earlier request in this session
  const hasSubmittedRef = useRef(false)

  useEffect(() => {
    if (
      hasSubmittedRef.current
      && !isLoadingSubmitServiceCase
      && submitServiceStatus === ACTION_STATUS.SUCCESS
    ) {
      hasSubmittedRef.current = false
      navigate(linkGenerator('/request-confirmation'))
    }
  }, [navigate, submitServiceStatus, isLoadingSubmitServiceCase])

  useEffect(() => {
    let faqs = []
    if (selectedFAQs && selectedFAQs.length > 0) {
      faqs = [...selectedFAQs]
    }

    if (failureDescriptionNotes) {
      faqs.push(failureDescriptionNotes)
    }
    setFailureDescriptionData(faqs)
  }, [selectedFAQs, failureDescriptionNotes])

  const requestButtonKey = useMemo(() => {
    switch (serviceEntry) {
      case SERVICE_ENTRY.REPAIR: {
        return 'requestServiceRepair'
      }
      case SERVICE_ENTRY.MAINTENANCE: {
        return 'requestServiceMaintenance'
      }
      case SERVICE_ENTRY.MACHINE: {
        return 'requestMachineService'
      }
      default: {
        return ''
      }
    }
  }, [serviceEntry])

  const selectedDeliveryAddress = useMemo(() => {
    if (deliveryAddress?.street) {
      // select new address data
      return deliveryAddress
    }
    return (
      partnerNumber
      && deliveryAddresses.find((item) => item?.partnerNumber === partnerNumber)
    )
  }, [partnerNumber, deliveryAddress, deliveryAddresses])

  const translatedSelectedServiceType = useMemo(() => {
    if (!selectedDesiredService) {
      return ''
    }
    return t(`service.serviceType.${selectedDesiredService.toLowerCase()}`)
  }, [t, selectedDesiredService])

  const mappingSubmitServiceValue = useMemo(() => {
    const images = (damagedImages || []).map(({ fileName, imageBase64 }) => ({
      fileName,
      imageBase64,
    }))

    const { street, street4, houseNumber } = selectedDeliveryAddress
    const mergeIntoString = mergeIntoStringWithSeparator(', ')

    const customerAddress = mergeIntoString(street, houseNumber, street4)
    const failureDescriptions = mergeIntoString(failureDescriptionData)

    const problemDescription = serviceEntry === SERVICE_ENTRY.MAINTENANCE
        || selectedDesiredService === SERVICE_TYPE.MAINTENANCE
      ? translatedSelectedServiceType
      : `${translatedSelectedServiceType}: ${failureDescriptions}`

    const foundCountryCode = countryNamesConfig.find(
      (config) => config.name === selectedDeliveryAddress.country,
    )

    return {
      contactPersonFirstName,
      contactPersonLastName,
      customerNumber: partnerNumber,
      title: 'New Service case created (B2B)',
      problemDescription,
      images,
      casePriority: SERVICE_CASE_PRIORITY.NORMAL,
      countryCode: foundCountryCode
        ? foundCountryCode.value
        : selectedDeliveryAddress.country,
      customerFirstName: selectedDeliveryAddress?.name || '',
      customerLastName: selectedDeliveryAddress?.name2 || '',
      customerCompanyName: selectedDeliveryAddress?.name,
      customerAddress,
      customerNote: selectedDeliveryAddress?.extraText,
      customerCity: selectedDeliveryAddress?.city,
      customerRegion: selectedDeliveryAddress?.region,
      customerZipCode: selectedDeliveryAddress?.zip,
      materialNumber: machineId,
      materialName: machineName,
      serialNumber,
      contactEmail: contactPersonEmail,
      caseType: selectedDesiredService,
      isKaercher: true,
      furtherNotes,
      contactPersonPhoneNumber,
      language,
      userId,
    }
  }, [
    damagedImages,
    selectedDeliveryAddress,
    failureDescriptionData,
    serviceEntry,
    selectedDesiredService,
    translatedSelectedServiceType,
    countryNamesConfig,
    contactPersonFirstName,
    contactPersonLastName,
    partnerNumber,
    machineId,
    machineName,
    serialNumber,
    contactPersonEmail,
    furtherNotes,
    contactPersonPhoneNumber,
    language,
    userId,
  ])

  const onNotesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setFurtherNotes(event.target.value)
  }

  const onRequestServiceHandler = useCallback(async () => {
    await form
      .validateFields(['serviceFurtherNotes'])
      .then(() => {
        if (
          mappingSubmitServiceValue.customerRegion
          && regions[mappingSubmitServiceValue.customerRegion]
        ) {
          mappingSubmitServiceValue.customerRegion =
            regions[mappingSubmitServiceValue.customerRegion].label
        }
        hasSubmittedRef.current = true
        dispatch(createServiceCase(mappingSubmitServiceValue))
      })
      .catch((errorInfo) => {
        if (isValidArray(errorInfo?.errorFields)) {
          NotificationManager.error({
            message: errorInfo?.errorFields.map((error) => {
              return (
                <span key={error?.name.join('-')} style={{ display: 'block' }}>
                  {error?.errors}
                </span>
              )
            }),
          })
        }
      })
  }, [dispatch, mappingSubmitServiceValue, regions, form])

  return (
    <div className="service-review">
      <Row gutter={[24, 35]}>
        <Col lg={16}>
          <Row gutter={[24, 35]}>
            <Col md={{ span: 12 }} xs={{ span: 24 }}>
              <ServiceBlockWrapper title={tLabel('machineSelected')}>
                <SelectedMachine data={selectedMachineData} />
              </ServiceBlockWrapper>
            </Col>
            <Col md={{ span: 12, order: 0 }} xs={{ span: 24, order: 2 }}>
              <ServiceBlockWrapper title={tLabel('contactInformation')}>
                <ContactInformation data={contactInformation} />
              </ServiceBlockWrapper>
            </Col>
            <Col md={{ span: 12, order: 0 }} xs={{ span: 24, order: 1 }}>
              <ServiceBlockWrapper title={tLabel('serviceSelected')}>
                <SelectedService serviceType={translatedSelectedServiceType} />
              </ServiceBlockWrapper>
            </Col>
            <Col md={{ span: 12 }} xs={{ span: 24, order: 3 }}>
              <ServiceBlockWrapper title={tLabel('deliveryAddress')}>
                <DeliveryAddress data={selectedDeliveryAddress} />
              </ServiceBlockWrapper>
            </Col>
          </Row>
        </Col>
        <Col
          lg={{ span: 8, push: 0 }}
          md={{ span: 12, push: 12 }}
          xs={{ span: 24 }}
        >
          <div
            className={clsx({
              'container-full-width-page': isMobile,
            })}
          >
            <div className="what-happens-next">
              <ServiceBlockWrapper title={tLabel('whatHappensNext')}>
                <WhatHappensNext />
              </ServiceBlockWrapper>
            </div>
          </div>
        </Col>
      </Row>
      {
        /* NOTED :
        if entry point is from machine or repair
        user has to select the service including the FAQs as optional field
        Hence, we only render this FAQs overview section for machine and repair entries
      */
      }
      {serviceEntry !== SERVICE_ENTRY.MAINTENANCE && (
        <Row gutter={[24, 35]}>
          <Divider />
          <Col lg={{ span: 8 }} md={{ span: 12 }} xs={{ span: 24 }}>
            <ServiceBlockWrapper title={tLabel('failureDescription')}>
              <FailureDescription data={failureDescriptionData || []} />
            </ServiceBlockWrapper>
          </Col>
          <Col
            lg={{ span: 8 }}
            md={{ span: 12, order: 0 }}
            xs={{ span: 24, order: 2 }}
          >
            <ServiceBlockWrapper title={tLabel('damagedPictures')}>
              <DamagedPicture data={damagedImages} />
            </ServiceBlockWrapper>
          </Col>
          <Col lg={{ span: 12 }} xs={{ span: 24, order: 1 }}>
            <Form.Item
              className="custom-form-item"
              form={form}
              required={isRequiredReference}
              label={
                <div className="FAQ__failures__notes-title">
                  <Translator
                    isComponent
                    value={tLabel('furtherNotes')}
                    htmlTags={[
                      <span
                        key="further-notes-title"
                        className={clsx(
                          isRequiredReference
                            ? 'FAQ__failures__notes-title--required'
                            : 'FAQ__failures__notes-title--optional',
                        )}
                      />,
                    ]}
                  />
                </div>
              }
              rules={[
                {
                  required: isRequiredReference,
                  message: t('form.validate.orderReferenceRequired'),
                },
              ]}
              name="serviceFurtherNotes"
            >
              <Input.TextArea
                style={{ resize: 'none' }}
                onChange={onNotesChangeHandler}
                placeholder={t(
                  'service.serviceOverview.placeHolder.furtherNotes',
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Divider />
      <Row gutter={[24, 24]}>
        <Col
          xs={{ span: 24 }}
          md={{ span: 12 }}
          lg={{ span: 8 }}
          className="service-overview__back-button"
        >
          <B2becButtonBack
            type="ghost"
            block
            onClick={() => {
              handleChangeTab(+currentTab - 1)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          >
            {t('service.serviceOverview.button.backToAddressDate')}
          </B2becButtonBack>
        </Col>
        <Col
          xs={{ span: 24 }}
          md={{ span: 12 }}
          lg={{ span: 8, push: 8 }}
          className="service-overview__request-button"
        >
          <B2becButton
            block
            onClick={onRequestServiceHandler}
            loading={isLoadingSubmitServiceCase}
            disabled={isLoadingSubmitServiceCase}
          >
            {t(`service.serviceOverview.button.${requestButtonKey}`)}
          </B2becButton>
        </Col>
      </Row>
    </div>
  )
}

export default ServiceOverviewStep
